/**
 * Monitoring utilities — Phase 13.4
 * CloudWatch RUM integration (13.4.1), error logging (13.4.2),
 * user analytics (13.4.3), document upload analytics (13.4.4).
 *
 * RUM client is initialised once in main.tsx via initRUM().
 * All other utilities are thin wrappers that no-op when RUM is unavailable.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

interface RUMClient {
  recordEvent(eventType: string, eventDetails: Record<string, unknown>): void;
  recordError(error: Error): void;
  recordPageView(pageId: string): void;
}

declare global {
  interface Window {
    __rumClient?: RUMClient;
  }
}

// ── 13.4.1 CloudWatch RUM initialisation ─────────────────────────────────────

export interface RUMConfig {
  applicationId: string;
  applicationVersion: string;
  applicationRegion: string;
  identityPoolId: string;
  endpoint: string;
  guestRoleArn: string;
}

/**
 * Initialise CloudWatch RUM. Call once from main.tsx.
 * Uses the aws-rum-web package loaded via CDN or npm.
 */
export async function initRUM(config: RUMConfig): Promise<void> {
  try {
    // Dynamic import so the bundle doesn't fail if the package isn't installed yet
    const { AwsRum } = await import('aws-rum-web');
    const rumClient = new AwsRum(config.applicationId, config.applicationVersion, config.applicationRegion, {
      sessionSampleRate: 1,
      identityPoolId: config.identityPoolId,
      endpoint: config.endpoint,
      guestRoleArn: config.guestRoleArn,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
    });
    window.__rumClient = rumClient;
  } catch {
    // RUM unavailable — silently degrade
  }
}

// ── 13.4.2 Error logging ──────────────────────────────────────────────────────

export function logError(error: Error, context?: Record<string, unknown>): void {
  console.error('[Neura]', error, context);
  window.__rumClient?.recordError(error);
}

// ── 13.4.3 User analytics ─────────────────────────────────────────────────────

export function trackPageView(pageId: string): void {
  window.__rumClient?.recordPageView(pageId);
}

export function trackEvent(eventType: string, details: Record<string, unknown> = {}): void {
  window.__rumClient?.recordEvent(eventType, details);
}

// ── 13.4.4 Document upload analytics ─────────────────────────────────────────

export const uploadAnalytics = {
  trackUploadAttempt(fileType: string, fileSizeBytes: number): void {
    trackEvent('document_upload_attempt', { fileType, fileSizeBytes });
  },
  trackUploadSuccess(fileType: string, processingDurationMs: number): void {
    trackEvent('document_upload_success', { fileType, processingDurationMs });
  },
  trackUploadFailure(fileType: string, reason: string): void {
    trackEvent('document_upload_failure', { fileType, reason });
  },
  trackEditContentClick(): void {
    trackEvent('document_upload_edit_content_click');
  },
  trackDownloadOriginalClick(articleId: string): void {
    trackEvent('document_upload_download_original', { articleId });
  },
};
